import Phaser from "phaser";
import { LruBudget } from "./LruBudget.logic";
import { resolveAssetUrl } from "../engine/assetRoot";

// Per-minigame GPU texture budget. Sized for a Passages crawler, whose baked
// viewpoints are full-screen frames (~3 MB each decoded at 1024x768 RGBA): a
// modest level has dozens of directed-edge frames, so the old 32 MB ceiling
// (~10 frames) silently dropped most of them. 256 MB ≈ 85 full-screen frames.
// Beyond that cap the budget evicts the least-recently-used *unpinned* texture
// rather than refusing the new load (the old behaviour, which left deeper
// viewpoints as permanent placeholders). Callers keep the currently-visible and
// one-step-reachable frames pinned via `pin()`; see LruBudget.logic.ts.
const MAX_TEXTURE_BYTES = 256 * 1024 * 1024; // 256 MB

export class ScopedLoader {
  private _scene: Phaser.Scene;
  private _prefix: string;
  // All loaded keys (textures + audio), for the getter and teardown. Byte
  // accounting, recency and pinning for textures live in `_budget`.
  private _loadedKeys: Set<string> = new Set();
  private _budget = new LruBudget(MAX_TEXTURE_BYTES);

  constructor(scene: Phaser.Scene, minigameId: string) {
    this._scene = scene;
    this._prefix = `mg_${minigameId}_`;
  }

  prefixKey(key: string): string {
    return `${this._prefix}${key}`;
  }

  get loadedKeys(): ReadonlySet<string> {
    return this._loadedKeys;
  }

  /** Mark a texture most-recently-used so LRU eviction spares it. */
  touch(key: string): void {
    this._budget.touch(this.prefixKey(key));
  }

  /** Replace the protected working set — pinned textures are never evicted. */
  pin(keys: Iterable<string>): void {
    this._budget.setPinned([...keys].map((k) => this.prefixKey(k)));
  }

  /**
   * Admit a freshly-loaded texture of `bytes`, evicting LRU unpinned textures as
   * needed. Returns false when it cannot fit even after evicting everything
   * unpinned (caller should drop the load). Mutates the budget and removes any
   * evicted textures from the scene.
   */
  private _admitTexture(prefixed: string, bytes: number): boolean {
    const plan = this._budget.planFor(bytes);
    if (!plan.fits) return false;
    for (const key of plan.evict) {
      if (this._scene.textures.exists(key)) this._scene.textures.remove(key);
      this._budget.remove(key);
      this._loadedKeys.delete(key);
    }
    this._budget.add(prefixed, bytes);
    this._loadedKeys.add(prefixed);
    return true;
  }

  loadImage(key: string, url: string): Promise<void> {
    const prefixed = this.prefixKey(key);
    if (this._scene.textures.exists(prefixed)) {
      this._budget.touch(prefixed);
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      // Persistent error handler — stays active until it sees the right key, then
      // removes itself. Using `on` instead of `once` so concurrent loads don't
      // consume each other's error events.
      const onError = (file: Phaser.Loader.File) => {
        if (file.key === prefixed) {
          this._scene.load.off("loaderror", onError);
          reject(new Error(`ScopedLoader: failed to load image "${key}" from "${url}"`));
        }
      };
      this._scene.load.image(prefixed, resolveAssetUrl(url));
      this._scene.load.once(`filecomplete-image-${prefixed}`, () => {
        this._scene.load.off("loaderror", onError);
        const tex = this._scene.textures.get(prefixed);
        const src = tex.getSourceImage();
        const bytes = (src.width ?? 0) * (src.height ?? 0) * 4;
        if (!this._admitTexture(prefixed, bytes)) {
          this._scene.textures.remove(prefixed);
          reject(new Error(`ScopedLoader: texture memory limit exceeded (${MAX_TEXTURE_BYTES} bytes)`));
          return;
        }
        resolve();
      });
      this._scene.load.on("loaderror", onError);
      this._scene.load.start();
    });
  }

  loadSpritesheet(key: string, url: string, frameWidth: number, frameHeight: number): Promise<void> {
    const prefixed = this.prefixKey(key);
    if (this._scene.textures.exists(prefixed)) {
      this._budget.touch(prefixed);
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const onError = (file: Phaser.Loader.File) => {
        if (file.key === prefixed) {
          this._scene.load.off("loaderror", onError);
          reject(new Error(`ScopedLoader: failed to load spritesheet "${key}" from "${url}"`));
        }
      };
      this._scene.load.spritesheet(prefixed, resolveAssetUrl(url), { frameWidth, frameHeight });
      this._scene.load.once(`filecomplete-spritesheet-${prefixed}`, () => {
        this._scene.load.off("loaderror", onError);
        const tex = this._scene.textures.get(prefixed);
        const src = tex.getSourceImage();
        const bytes = (src.width ?? 0) * (src.height ?? 0) * 4;
        if (!this._admitTexture(prefixed, bytes)) {
          this._scene.textures.remove(prefixed);
          reject(new Error(`ScopedLoader: texture memory limit exceeded`));
          return;
        }
        resolve();
      });
      this._scene.load.on("loaderror", onError);
      this._scene.load.start();
    });
  }

  loadAudio(key: string, url: string): Promise<void> {
    const prefixed = this.prefixKey(key);
    if (this._scene.cache.audio.exists(prefixed)) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const onError = (file: Phaser.Loader.File) => {
        if (file.key === prefixed) {
          this._scene.load.off("loaderror", onError);
          reject(new Error(`ScopedLoader: failed to load audio "${key}" from "${url}"`));
        }
      };
      this._scene.load.audio(prefixed, resolveAssetUrl(url));
      this._scene.load.once(`filecomplete-audio-${prefixed}`, () => {
        this._scene.load.off("loaderror", onError);
        this._loadedKeys.add(prefixed);
        resolve();
      });
      this._scene.load.on("loaderror", onError);
      this._scene.load.start();
    });
  }

  destroy(): void {
    for (const key of this._loadedKeys) {
      if (this._scene.textures.exists(key)) {
        this._scene.textures.remove(key);
      }
    }
    this._loadedKeys.clear();
    this._budget.clear();
  }
}
